//region :: Imports ::
import {MDarkThemeIconName, MLightThemeIconName} from './Icons';
import {InteractionRequiredAuthError, IPublicClientApplication, SilentRequest} from '@azure/msal-browser';
import {IMsalContext} from '@azure/msal-react';
import axios, {AxiosHeaders} from 'axios';
import {FieldProps} from 'formik';

//endregion

export const _ = require('lodash');
export const luxon = require('luxon');

/** */
export type MNullable<T> = T | null;
export type MColor = 'mcolor-primary' | 'mcolor-secondary' | 'mcolor-success' | 'mcolor-info' | 'mcolor-warning' | 'mcolor-danger' | 'mcolor-help';
export type MEnumItem = { name: string, value: number };
export type MUITheme = 'light' | 'dark';
export type MUnhandledError = { errorMessage: MNullable<string>, exception: MNullable<any> };

/** */
export const delay = (milliseconds: number): Promise<void> => {

    return new Promise(
        resolve => setTimeout(resolve, milliseconds));

};

/** */
export const isNullOrUndefined = (value?: any): boolean => {
    return value === null || value === undefined;
};

/** */
export const copyToClipboard = (value?: string): Promise<void> => {
    return navigator.clipboard.writeText(value === undefined ? '' : value!);
};

/** */
export const navigateToRoot = (): void => {
    window.location.href = '/';
};

/** */
export const reloadPage = (): void => {
    window.location.reload();
};

/** */
export const looksLikeDate = (value: string): boolean => {
    return value.match(/^20[0-9]{2}-[0-9]{1,2}-[0-9]{1,2}T[0-9]{1,2}:[0-9]{1,2}/) && luxon.DateTime.fromISO(value).isValid;
};

/** */
export const formatDateAsText = (value: Date): string | undefined => {
    return isNullOrUndefined(value) ? undefined : `${value.toLocaleDateString('en-GB')} ${value.toLocaleTimeString()}`;
};

/** */
export const appendIfNeeded = (str: string, value: string): string => {
    return value.length === 0 || str.endsWith(value.toLowerCase()) ? str : `${str}${value}`;
};

/** */
export const getEnumItems = (enumType: object): MEnumItem[] => {

    const enumVar = enumType as any;
    const items: MEnumItem[] = [];

    for(let enumProp in enumVar) {

        if(isNaN(Number(enumProp))) {
            continue;
        }

        const name = enumVar[enumProp];
        const value = Number(enumProp);

        items.push({name: name, value: value} as MEnumItem);

    }

    return items;

};

/** */
export const toErrorMessage = (exception: any): string => {

    let status = exception.status ? ` (${exception.status})` : '';
    let message = isNullOrUndefined(exception.message) ? 'An unexpected server error occurred.' : exception.message;

    if(exception.status) {

        switch(exception.status) {

            case 400:
                message = exception.response.title !== undefined ? exception.response.title : exception.message;
                break;

            case 404:
                message = exception.response;
                break;

            case 409:
                message = exception.response;
                break;

        }

    }

    return `${message}${status}`;

};

/** */
export const unpackUnhandledError = (error: any): MUnhandledError => {

    const isObject = error instanceof Object;
    const errorMessage = isObject ? toErrorMessage(error) : error as MNullable<string>;
    const exception = isObject ? error : null;

    return {errorMessage, exception};

};

/** */
export const forEachProperty = (target: object, source: object, customizer?: (value: any) => any): any => {

    const defaultCustomizer = (value: any) => _.isString(value) && looksLikeDate(value) ? new Date(Date.parse(value)) : value;
    const valueCustomizer = customizer !== undefined ? customizer : defaultCustomizer;

    return _.assignWith(
        target,
        source,
        (targetValue: any, sourceValue: any, _propertyKey: string) => {

            const isArray = Array.isArray(sourceValue);
            const isObject = _.isObject(sourceValue);

            if(!isArray && !isObject) {
                return valueCustomizer(sourceValue);
            } else {

                if(isArray) {
                    return forEachProperty(Array.from(Array(sourceValue.length)), (sourceValue as any[]), customizer);
                }

                if(isObject) {
                    return forEachProperty({}, sourceValue, customizer);
                }

            }

        });

};

/** */
export const getAuthAccessToken = async(context: IMsalContext, scopes: string[]): Promise<string> => {

    const account = context.instance.getActiveAccount();
    const request = {account: account, scopes: scopes} as SilentRequest;

    try {
        return (await context.instance.acquireTokenSilent(request)).accessToken;
    } catch(ex) {

        if(ex instanceof InteractionRequiredAuthError) {
            await context.instance.acquireTokenRedirect(request);
        } else {
            throw ex;
        }
    }

    return '';

};

/** */
export const hasRoles = (roles: string[] | undefined, msalContext: IPublicClientApplication): boolean => {

    if(roles === undefined || roles.length === 0) {
        return false;
    }

    const account = msalContext.getActiveAccount();
    const accountRoles = account?.idTokenClaims?.roles;

    if(account === undefined || accountRoles === undefined || accountRoles.length === 0) {
        return false;
    }

    return roles.filter((role: string) => accountRoles.includes(role)).length > 0;

};

/** */
export const getUIThemeFromStorage = (): MUITheme => {

    const theme = window.localStorage.getItem('uitheme') as MUITheme;
    return isNullOrUndefined(theme) ? 'light' : theme!;

};

/** */
export const getNextUITheme = (): MUITheme => {
    return getUIThemeFromStorage() === 'light' ? 'dark' : 'light';
};

/** */
export const getUIThemeIcon = (): string => {
    return getUIThemeFromStorage() === 'light' ? MDarkThemeIconName : MLightThemeIconName;
};

/** */
export const loadUITheme = (): void => {
    changeUITheme(getUIThemeFromStorage());
};

/** */
export const toggleUITheme = (): void => {
    changeUITheme(getNextUITheme());
};

/** */
export const changeUITheme = (theme: MUITheme): void => {

    let themeLink = document.getElementById('app-uitheme') as HTMLLinkElement;

    if(themeLink === undefined) {
        throw Error('Could not find the UI theme \'link\' element in the \'index.html\' page.');
    }

    const lastSlashIndex = themeLink.href.lastIndexOf('/');
    const baseUrl = themeLink.href.substring(0, lastSlashIndex + 1);

    themeLink.href = `${baseUrl}${theme}.css`;
    window.localStorage.setItem('uitheme', theme);

};

/** */
export const getDynODataHeaders = (accessToken: string, maxPageSize: MNullable<number>): AxiosHeaders => {

    const headers = new AxiosHeaders();

    headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('Prefer', maxPageSize === null ? '' : `odata.maxpagesize=${maxPageSize}`);
    headers.set('Accept', 'application/json');
    headers.set('Content-Type', 'application/json; charset=utf-8');
    headers.set('OData-Version', '4.0');
    headers.set('OData-MaxVersion', '4.0');

    return headers;

};

/** */
export const getDynAccessToken = async(context: IMsalContext, environmentUrl: string): Promise<string> => {
    return await getAuthAccessToken(context, [`${appendIfNeeded(environmentUrl, '/')}.default`]);
};

/** */
export const getDynEntity = async(
    environmentUrl: string,
    entitySetName: string,
    entityId: string,
    accessToken: string,
    abortSignal: AbortSignal | undefined): Promise<any> => {

    const headers = getDynODataHeaders(accessToken, null);
    const url = `${environmentUrl}/api/data/v9.2/${entitySetName}(${entityId})`;
    const response = await axios.get(url, {headers: headers, signal: abortSignal});

    return response.data;

};

/** */
export const getDynPagedEntities = async(
    environmentUrl: string,
    entitySetName: string,
    query: MNullable<string>,
    maxPageSize: number,
    accessToken: string,
    abortSignal: AbortSignal | undefined): Promise<{ count: number, nextLink: string | undefined, value: any[] }> => {

    const headers = getDynODataHeaders(accessToken, maxPageSize);
    let url = `${environmentUrl}/api/data/v9.2/${entitySetName}/?$count=true`;

    if(query !== null && query.length > 0) {
        url = `${url}&${query}`;
    }

    const response = await axios.get(url, {
        headers: headers,
        signal: abortSignal
    });

    const count = _.get(response.data, '@odata.count');
    const nextLink = _.get(response.data, '@odata.nextLink');

    return {count: count, nextLink: nextLink, value: response.data.value};

};

/** */
export const getDynNextPagedEntities = async(
    nextLinkUrl: string,
    maxPageSize: number,
    accessToken: string,
    abortSignal: AbortSignal | undefined): Promise<{ count: number, nextLink: string | undefined, value: any[] }> => {

    const response = await axios.get(nextLinkUrl, {
        headers: getDynODataHeaders(accessToken, maxPageSize),
        signal: abortSignal
    });

    const count = _.get(response.data, '@odata.count');
    const nextLink = _.get(response.data, '@odata.nextLink');

    return {count: count, nextLink: nextLink, value: response.data.value};

};

/** */
export const getDynEntityMetadata = async(
    environmentUrl: string,
    entityLogicalName: string,
    accessToken: string,
    abortSignal: AbortSignal | undefined): Promise<any> => {

    const headers = getDynODataHeaders(accessToken, null);
    const url = `${environmentUrl}/api/data/v9.2/EntityDefinitions(LogicalName='${entityLogicalName}')`;
    const response = await axios.get(url, {headers: headers, signal: abortSignal});

    response.data.Labels = {
        DisplayName: response.data.DisplayName.UserLocalizedLabel !== null ? response.data.DisplayName.UserLocalizedLabel.Label : undefined,
        Description: response.data.Description.UserLocalizedLabel !== null ? response.data.Description.UserLocalizedLabel.Label : undefined
    };

    return response.data;

};

/** */
export const getDynEntityFieldMetadata = async(
    environmentUrl: string,
    entityLogicalName: string,
    fieldNames: MNullable<string[]>,
    accessToken: string,
    abortSignal: AbortSignal | undefined): Promise<any[]> => {

    const headers = getDynODataHeaders(accessToken, null);
    let url = `${environmentUrl}/api/data/v9.2/EntityDefinitions(LogicalName='${entityLogicalName}')/Attributes`;

    if(fieldNames !== null && fieldNames.length > 0) {

        const propertyValues = _.join(fieldNames.map((fieldName: string) => `'${fieldName}'`), ',');
        const filterQuery = `$filter=IsPrimaryName eq true or Microsoft.Dynamics.CRM.In(PropertyName='LogicalName',PropertyValues=[${propertyValues}])`;

        url = `${url}?${filterQuery}`;

    }

    const response = await axios.get(url, {
        headers: headers,
        signal: abortSignal
    });

    response.data.value = response.data.value.map((fieldMetadata: any) => {

        fieldMetadata.Labels = {
            DisplayName: fieldMetadata.DisplayName.UserLocalizedLabel !== null ? fieldMetadata.DisplayName.UserLocalizedLabel.Label : undefined,
            Description: fieldMetadata.Description.UserLocalizedLabel !== null ? fieldMetadata.Description.UserLocalizedLabel.Label : undefined
        };

        return fieldMetadata;

    });

    return response.data.value;

};

/** */
export const setFieldValue = (fieldProps: FieldProps | undefined, newValue: any): void => {

    if(fieldProps === undefined) {
        return;
    }

    fieldProps.form.setFieldValue(fieldProps.field.name, newValue);
    fieldProps.form.handleChange(fieldProps.field.name);

};
